import React, { useEffect, useMemo, useState } from 'react';
import { Dropdown, MenuProps, Space } from 'antd';
import { DownOutlined, CheckOutlined } from '@ant-design/icons';
import { useAtom } from 'jotai';
import { COLORS } from 'themes/colors';
import { languagesAtom, selectedLanguageAtom, LanguageOption } from '../../store';

declare global {
  interface Window {
    google?: any;
    googleTranslateElementInit?: () => void;
  }
}

const HIDDEN_CONTAINER_ID = 'google_translate_element_container';

function injectGoogleTranslateScript(): void {
  if (document.getElementById('google-translate-script')) return;
  const script = document.createElement('script');
  script.id = 'google-translate-script';
  script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
  script.async = true;
  document.body.appendChild(script);
}

// We will be programatically controlling the google translate element with our custom language dropdown,
// because the gte has limited styling. This is why we need to hide the element off screen.
function ensureHiddenContainer(): void {
  if (document.getElementById(HIDDEN_CONTAINER_ID)) return;
  const div = document.createElement('div');
  div.id = HIDDEN_CONTAINER_ID;
  // web development standard for hiding elements off-screen while not removing them entirely
  div.style.position = 'absolute';
  div.style.left = '-9999px';
  div.style.top = '0';
  div.style.width = '1px';
  div.style.height = '1px';
  document.body.appendChild(div);
}

// Initializes the Google Translate element if not already present
function initGoogleElement(pageLanguage = 'en'): void {
  if (!window.google || !window.google.translate || !window.google.translate.TranslateElement) return;
  // already initialized if the combo exists in our container
  if (document.querySelector(`#${HIDDEN_CONTAINER_ID} select.goog-te-combo`)) return;
  new window.google.translate.TranslateElement({
    pageLanguage,
    autoDisplay: false,
    layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
  }, HIDDEN_CONTAINER_ID);
}

/*
  This cookie is to optimize user performance. It persists the user's language choice across page loads,
  and auto translates on page load. But the core functionality doesn't depend on it, hens the no-op silent catch
*/
function setGoogTransCookie(targetLang: string) {
  try {
    const host = window.location.hostname;
    const value = `/auto/${targetLang}`;
    const base = `googtrans=${value}; path=/`;

    // Always set host-only cookie (no domain attribute)
    document.cookie = base;

    // Compute additional domain scopes to ensure Google picks the intended value first
    const domains: string[] = [];
    // Current host as a dotted domain
    if (host && !host.startsWith('.')) domains.push(`.${host}`);
    // Parent/root domain when applicable
    const parts = host.split('.');
    if (parts.length > 2) {
      const root = parts.slice(-2).join('.');
      // Avoid setting invalid domains like .localhost
      if (root && root.includes('.')) domains.push(`.${root}`);
    }

    domains.forEach((d) => {
      document.cookie = `${base}; domain=${d}`;
    });
  }
  catch { /* noop */ }
}

// Clears the Google Translate cookie so we can set a new one
function clearGoogTransCookie() {
  try {
    const host = window.location.hostname;
    const base = 'googtrans=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    // Clear host-only
    document.cookie = base;
    // Clear at host and parent/root domains
    const domains: string[] = [];
    if (host && !host.startsWith('.')) domains.push(`.${host}`);
    const parts = host.split('.');
    if (parts.length > 2) {
      const root = parts.slice(-2).join('.');
      if (root && root.includes('.')) domains.push(`.${root}`);
    }
    domains.forEach((d) => {
      document.cookie = `${base}; domain=${d}`;
    });
  }
  catch { /* noop */ }
}

const LanguageDropdown: React.FC = () => {
  const [languages] = useAtom(languagesAtom);
  const [selected, setSelected] = useAtom(selectedLanguageAtom);
  const [isOpen, setIsOpen] = useState(false);

  // Prepare menu items
  const items: MenuProps['items'] = useMemo(() => (
    languages.map((l: LanguageOption) => ({
      key: l.code,
      label: (
        <div className="language-dropdown-menu-item-content notranslate" translate="no" data-cy={`language-dropdown-item-${l.code}`}>
          <Space className="notranslate" translate="no">
            <span aria-hidden="true" className="notranslate" translate="no">{l.flag || ''}</span>
            <span className="notranslate" translate="no">{l.nativeText || l.name}</span>
          </Space>
          {selected === l.code && (
            <CheckOutlined style={{ color: COLORS.cipherGreenL0 }} className="language-dropdown-checkmark notranslate" />
          )}
        </div>
      ),
    }))
  ), [languages, selected]);

  useEffect(() => {
    // If no provider languages, do not initialize Google Translate
    if (!languages?.length) return;
    ensureHiddenContainer();
    const init = () => {
      initGoogleElement('en');
    };
    window.googleTranslateElementInit = init;
    if ((window as any).google?.translate?.TranslateElement) {
      init();
    }
    else {
      injectGoogleTranslateScript();
    }
  }, [languages]);

  const onClick: MenuProps['onClick'] = ({ key }) => {
    const code = String(key);
    // Persist choice to state for UI
    setSelected(code);
    if (code === 'en') {
      // Clear translate cookie and perform a soft reload to apply immediately
      clearGoogTransCookie();
      window.location.reload();
      return;
    }
    // Clear any stale cookies across domains first, then set anew and reload
    clearGoogTransCookie();
    setGoogTransCookie(code);
    window.location.reload();
  };

  const selectedLanguage = languages.find(l => l.code === selected);

  if (!languages?.length) return null;

  return (
    <Dropdown
      arrow
      menu={{
        items,
        onClick,
        className: 'notranslate language-dropdown-menu',
        rootClassName: 'notranslate',
      }}
      trigger={['click']}
      placement="bottomRight"
      onOpenChange={setIsOpen}
    >
      <div
        className={`notranslate language-dropdown-button ${isOpen ? 'language-dropdown-open' : ''}`}
        translate="no"
        aria-label="Select language"
        role="button"
        tabIndex={0}
        data-cy="language-dropdown-button"
      >
        <div className="language-dropdown-content">
          <span aria-hidden="true" className="notranslate" translate="no">
            {selectedLanguage?.flag || '🌐'}
          </span>
          <span className="notranslate" translate="no">
            {selectedLanguage?.nativeText || selectedLanguage?.name || 'Language'}
          </span>
        </div>
        <DownOutlined className="language-dropdown-chevron" />
      </div>
    </Dropdown>
  );
};

export default LanguageDropdown;
